/*
 *   File : store-history.js
 *   Author : https://evoqins.com
 *   Description : Sales specialist store assignment history
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';

// Custom components
import { DataTableComponent } from '../../../../Component/DataTable';
import { APIService } from '../../../../Service';
import { Loader } from '../../../../Component/Others';
import { EmptyScreen } from '../../../../Component/EmptyComponent';

const ITEMS_PER_PAGE = 10;

const PAGE_SORT_FIELDS = [
    'store_name',
    'from_date',
    'to_date',
    'assigned_by',
];

const StoreHistory = (props) => {

    const [historyData, setHistoryData] = useState({ data: [], total_count: 0 });
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    useEffect(() => {
        if (props.customerId == null) {
            return;
        }

        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }

        _getStoreHistory();
    }, [props.customerId, currentPage]);

    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setLoading(true);
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getStoreHistory(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const TABLE_COLUMNS = [
        {
            name: 'Store name',
            id: 'store_name',
            sortField: 'store_name',
            selector: row => row.store_name || "-",
            width: "280px",
            sortable: true,
        },
        {
            name: 'From date',
            id: 'from_date',
            sortField: 'from_date',
            selector: row => row.from_date || "-",
            width: "160px",
            sortable: true,
        },
        {
            name: 'To date',
            id: 'to_date',
            sortField: 'to_date',
            selector: row => row.to_date || "-",
            width: "160px",
            sortable: true,
        },
        {
            name: 'Assigned by',
            id: 'assigned_by',
            sortField: 'assigned_by',
            selector: row => row.assigned_by || "-",
            width: "200px",
            sortable: true,
        },
    ];

    // API - Get store assignment history
    const _getStoreHistory = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/sales-specialist/store-assignment-history';
        let data = {
            "customer_id": props.customerId,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const list = Array.isArray(response.data?.data)
                    ? response.data.data
                    : (Array.isArray(response.data) ? response.data : []);
                setHistoryData({
                    data: list,
                    total_count: response.data?.total_count ?? list.length,
                });
            } else {
                setHistoryData({ data: [], total_count: 0 });
            }
            setLoading(false);
        }).catch(() => {
            setHistoryData({ data: [], total_count: 0 });
            setLoading(false);
        });
    };

    const tableRows = Array.isArray(historyData?.data) ? historyData.data : [];

    return (
        <div className='e-admin-box-shadow e-bg-white e-border-radius-16 py-4'>
            <div className='px-4'>
                <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                    Store change history
                </h4>
            </div>

            {loading ?
                <Loader />
                :
                <>
                    {tableRows.length !== 0 ?
                        <div className='e-members-detail-table pt-3'>
                            <DataTableComponent
                                key={`store-history-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                columns={TABLE_COLUMNS}
                                data={tableRows}
                                pagination={true}
                                paginationServer={true}
                                paginationPerPage={ITEMS_PER_PAGE}
                                paginationTotalRows={historyData.total_count}
                                paginationDefaultPage={currentPage}
                                paginationResetDefaultPage={false}
                                sortServer={true}
                                defaultSortFieldId={sortField}
                                defaultSortAsc={sortDirection !== 'desc'}
                                onSort={_handleSort}
                                onChangePage={_handlePageChange} />
                        </div>
                        :
                        <div className='py-5 my-5'>
                            <EmptyScreen message="No store history found" />
                        </div>
                    }
                </>
            }
        </div>
    );
};

export default StoreHistory;
